import React, { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import axios from 'axios'
import { API_BASE_URL } from '../utils/api'
import LoadingSpinner from './LoadingSpinner'

interface BrokerContext {
  brokerId: number
  brokerName: string
  brokerEmail: string
  collaboratorId: number
  status: string
  accessLevel: string
  lastAccess: string
  collaborationTitle?: string
}

interface ActiveBrokerContext {
  brokerId: number
  brokerName: string
  collaboratorId: number
  accessLevel: string
  permissions: any
}

export const BrokerContextSwitcher: React.FC = () => {
  const { user } = useAuth()
  const [availableBrokers, setAvailableBrokers] = useState<BrokerContext[]>([])
  const [activeBrokerContext, setActiveBrokerContext] = useState<ActiveBrokerContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [error, setError] = useState('')

  // Only show for collaborators
  if (!user || user.role !== 'collaborator') {
    return null
  }

  useEffect(() => {
    fetchAvailableBrokers()
    fetchCurrentContext()
  }, [])

  const fetchAvailableBrokers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/broker-context/available`)
      setAvailableBrokers(response.data.availableBrokers || [])
    } catch (error: any) {
      console.error('Error fetching available brokers:', error)
      setError('Failed to load available broker contexts')
    }
  }

  const fetchCurrentContext = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/broker-context/current`)
      setActiveBrokerContext(response.data.activeBrokerContext)
    } catch (error: any) {
      // It's ok if there's no current context
      setActiveBrokerContext(null)
    } finally {
      setLoading(false)
    }
  }

  const switchBrokerContext = async (brokerId: number) => {
    try {
      setSwitching(true)
      setError('')
      
      const response = await axios.post(`${API_BASE_URL}/api/broker-context/switch/${brokerId}`)
      setActiveBrokerContext(response.data.activeBrokerContext)
      setShowDropdown(false)
      
      // Refresh the page to ensure all data is updated for the new context
      window.location.reload()
      
    } catch (error: any) {
      console.error('Error switching broker context:', error)
      setError(error.response?.data?.message || 'Failed to switch broker context')
    } finally {
      setSwitching(false)
    }
  }

  const getAccessLevelBadge = (level: string) => {
    const levelConfig = {
      'read_only': { color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300', icon: '👁️' },
      'standard': { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', icon: '👤' },
      'elevated': { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', icon: '⭐' },
      'manager': { color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', icon: '👑' }
    }
    
    const config = levelConfig[level as keyof typeof levelConfig] || levelConfig.standard
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <span className="mr-1">{config.icon}</span>
        {level.replace('_', ' ').toUpperCase()}
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <LoadingSpinner />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading brokers...</span>
      </div>
    )
  }

  if (availableBrokers.length === 0) {
    return (
      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
        No broker contexts available
      </div>
    )
  }

  if (availableBrokers.length === 1 && !activeBrokerContext) {
    // Auto-switch to the only available broker
    switchBrokerContext(availableBrokers[0].brokerId)
    return (
      <div className="flex items-center space-x-2 px-3 py-2">
        <LoadingSpinner />
        <span className="text-sm text-gray-500 dark:text-gray-400">Connecting...</span>
      </div>
    )
  }

  return (
    <div className="relative">
      {/* Current Context Display */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-3 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 min-w-[200px]"
        disabled={switching}
      >
        <div className="flex items-center space-x-2 flex-1">
          {activeBrokerContext ? (
            <>
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                <span className="text-gray-900 dark:text-gray-600 font-medium text-xs">
                  {activeBrokerContext.brokerName.split(' ').map(n => n[0]).join('')}
                </span>
              </div>
              <div className="flex-1 text-left">
                <div className="font-medium text-gray-900 dark:text-white truncate">
                  {activeBrokerContext.brokerName}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {getAccessLevelBadge(activeBrokerContext.accessLevel)}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 8h1m-1-4h1m4 4h1m-1-4h1" />
                </svg>
              </div>
              <span className="text-gray-500 dark:text-gray-400">Select broker context</span>
            </>
          )}
        </div>
        
        <svg 
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
          {error && (
            <div className="p-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-b border-gray-200 dark:border-gray-700">
              {error}
            </div>
          )}
          
          <div className="py-2">
            {availableBrokers.map((broker) => (
              <button
                key={broker.brokerId}
                onClick={() => switchBrokerContext(broker.brokerId)}
                disabled={switching || activeBrokerContext?.brokerId === broker.brokerId}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                  activeBrokerContext?.brokerId === broker.brokerId 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-r-2 border-gray-900' 
                    : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <span className="text-gray-900 dark:text-gray-600 font-medium text-sm">
                      {broker.brokerName.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-gray-900 dark:text-white truncate">
                        {broker.brokerName}
                      </div>
                      {activeBrokerContext?.brokerId === broker.brokerId && (
                        <div className="text-gray-700 ml-2">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {broker.brokerEmail}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {getAccessLevelBadge(broker.accessLevel)}
                      {broker.collaborationTitle && (
                        <span className="text-xs text-gray-400 truncate ml-2">
                          {broker.collaborationTitle}
                        </span>
                      )}
                    </div>
                    {broker.lastAccess && (
                      <div className="text-xs text-gray-400 mt-1">
                        Last accessed: {new Date(broker.lastAccess).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {activeBrokerContext && (
            <div className="border-t border-gray-200 dark:border-gray-700 p-2">
              <button
                onClick={() => {
                  // Logout from current context
                  axios.post(`${API_BASE_URL}/api/broker-context/logout`)
                    .then(() => {
                      setActiveBrokerContext(null)
                      setShowDropdown(false)
                      window.location.reload()
                    })
                    .catch(console.error)
                }}
                className="w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
              >
                Logout from current context
              </button>
            </div>
          )}
        </div>
      )}
      
      {switching && (
        <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}
